import { Alert, Button, Input, Label, Modal } from '@lifebit-ai/lds'
import { Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import {
  REQUEST_ADDITIONAL_INFORMATION_MODAL_TEST_ID,
  REQUEST_ADDITIONAL_INFORMATION_REASON_FIELD_TEST_ID,
  REQUEST_ADDITIONAL_INFORMATION_SUBMIT_TEST_ID
} from 'modules/resultsV2/utils'

import * as styles from './RequestAdditionalInformationModal.styles'

export const ADDITIONAL_INFORMATION_REASON_MAX_LENGTH = 1000

const validationSchema = Yup.object().shape({
  reason: Yup.string()
    .required('Reason is required')
    .max(
      ADDITIONAL_INFORMATION_REASON_MAX_LENGTH,
      `Reason must be ${ADDITIONAL_INFORMATION_REASON_MAX_LENGTH} characters or fewer`
    )
})

interface RequestAdditionalInformationModalProps {
  action: (reason: string) => void
  description: string
  isError: boolean
  isLoading: boolean
  isOpen?: boolean
  onClose: () => void
  title?: string
  okTitle?: string
}

export const RequestAdditionalInformationModal = ({
  action,
  description,
  isError,
  isLoading,
  isOpen,
  okTitle = 'Request additional information',
  onClose,
  title = 'Request additional information?'
}: RequestAdditionalInformationModalProps): JSX.Element => {
  const onSubmit = (values: { reason: string }): void => {
    action(values.reason.trim())
  }

  return (
    <Modal
      data-testid={REQUEST_ADDITIONAL_INFORMATION_MODAL_TEST_ID}
      destroyOnClose
      footer={null}
      open={isOpen}
      size="small"
      title={title}
      closable
      onCancel={onClose}
    >
      <styles.Wrapper>
        <styles.Description>{description}</styles.Description>

        <Formik<{ reason: string }>
          initialValues={{ reason: '' }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ getFieldProps, values }) => (
            <Form>
              <styles.Body>
                {isError && (
                  <styles.ErrorMsg>
                    <Alert showIcon type="danger">
                      There was an error while requesting additional information. Please try again or contact the
                      administrator.
                    </Alert>
                  </styles.ErrorMsg>
                )}

                <Input
                  textArea
                  data-testid={REQUEST_ADDITIONAL_INFORMATION_REASON_FIELD_TEST_ID}
                  id="reason"
                  label={<Label>Please specify the information needed</Label>}
                  isRequired
                  maxLength={ADDITIONAL_INFORMATION_REASON_MAX_LENGTH}
                  placeholder="Reason"
                  {...getFieldProps('reason')}
                />
              </styles.Body>

              <styles.Footer>
                <Button
                  data-testid={REQUEST_ADDITIONAL_INFORMATION_SUBMIT_TEST_ID}
                  disabled={values.reason.trim().length === 0}
                  htmlType="submit"
                  loading={isLoading}
                  size="large"
                  type="primary"
                >
                  {okTitle}
                </Button>
              </styles.Footer>
            </Form>
          )}
        </Formik>
      </styles.Wrapper>
    </Modal>
  )
}

export default RequestAdditionalInformationModal
